(() => {
  if (window.GoyojoTechnologyTabs) {
    window.GoyojoTechnologyTabs.init(document);
    return;
  }

  class TechnologyFeatureTabs {
    constructor(root) {
      this.root = root;
      this.tabs = Array.from(root.querySelectorAll('[role="tab"][data-tab-index]'));
      this.panels = Array.from(root.querySelectorAll('[role="tabpanel"][data-panel-index]'));
      this.viewport = root.querySelector('[data-tab-viewport]');
      this.previousButton = root.querySelector('[data-tab-previous]');
      this.nextButton = root.querySelector('[data-tab-next]');
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.transitionTimer = null;
      this.activationFrame = null;
      this.autoplayTimer = null;
      this.gesture = null;
      this.hovering = false;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      const requestedIndex = Number.parseInt(root.dataset.initialIndex || '0', 10);
      this.currentIndex = this.clampIndex(Number.isNaN(requestedIndex) ? 0 : requestedIndex);
      this.transitionType = root.dataset.transitionType || 'fade-and-slide';
      this.duration = this.reducedMotionQuery.matches
        ? 0
        : Math.max(0, Number.parseInt(root.dataset.transitionDuration || '400', 10));
      this.autoplayEnabled = root.dataset.autoplay === 'true' && !this.reducedMotionQuery.matches;
      this.autoplayInterval = Math.max(3000, Number.parseInt(root.dataset.autoplayInterval || '6000', 10));
      this.pauseOnHover = root.dataset.pauseOnHover === 'true';
      this.loopNavigation = root.dataset.loopNavigation === 'true';
      this.swipeEnabled = root.dataset.enableSwipe === 'true';
      this.centerActiveTab = root.dataset.centerActiveTab === 'true';

      this.bindEvents();
      this.syncInitialState();
      this.startAutoplay();
    }

    clampIndex(index) {
      if (!this.tabs.length) return 0;
      return Math.min(Math.max(index, 0), this.tabs.length - 1);
    }

    normalizedAdjacentIndex(index) {
      if (!this.tabs.length) return 0;
      if (this.loopNavigation) {
        return (index + this.tabs.length) % this.tabs.length;
      }
      return this.clampIndex(index);
    }

    syncInitialState() {
      this.tabs.forEach((tab, index) => {
        const active = index === this.currentIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel, index) => {
        const active = index === this.currentIndex;
        panel.classList.toggle('is-active', active);
        panel.classList.remove('is-exiting');
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });

      this.updateArrowState();
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const tab = event.target.closest('[role="tab"][data-tab-index]');
        if (tab && this.root.contains(tab)) {
          const index = Number.parseInt(tab.dataset.tabIndex, 10);
          this.activate(index, { userInitiated: true, scroll: true });
          return;
        }

        if (event.target.closest('[data-tab-previous]')) {
          this.activateAdjacent(-1, true);
        } else if (event.target.closest('[data-tab-next]')) {
          this.activateAdjacent(1, true);
        }
      }, { signal: this.signal });

      this.root.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[role="tab"][data-tab-index]');
        if (!tab || !this.root.contains(tab)) return;

        const tabIndex = Number.parseInt(tab.dataset.tabIndex, 10);
        let targetIndex = null;

        if (event.key === 'ArrowLeft') targetIndex = this.normalizedAdjacentIndex(tabIndex - 1);
        if (event.key === 'ArrowRight') targetIndex = this.normalizedAdjacentIndex(tabIndex + 1);
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = this.tabs.length - 1;

        if (targetIndex !== null) {
          event.preventDefault();
          this.activate(targetIndex, { userInitiated: true, focus: true, scroll: true });
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.activate(tabIndex, { userInitiated: true, focus: true, scroll: true });
        }
      }, { signal: this.signal });

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', () => {
          this.hovering = true;
          this.stopAutoplay();
        }, { signal: this.signal });

        this.root.addEventListener('mouseleave', () => {
          this.hovering = false;
          this.startAutoplay();
        }, { signal: this.signal });
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      }, { signal: this.signal });

      window.addEventListener('resize', () => {
        this.updateArrowState();
        this.scrollActiveTab(false);
      }, { signal: this.signal });

      this.reducedMotionQuery.addEventListener('change', (event) => {
        if (event.matches) {
          this.duration = 0;
          this.autoplayEnabled = false;
          this.stopAutoplay();
        } else {
          this.duration = Math.max(0, Number.parseInt(this.root.dataset.transitionDuration || '400', 10));
          this.autoplayEnabled = this.root.dataset.autoplay === 'true';
          this.startAutoplay();
        }
      }, { signal: this.signal });

      if (this.swipeEnabled) {
        this.root.addEventListener('pointerdown', (event) => {
          if (!event.isPrimary) {
            this.clearGesture();
            return;
          }
          if (event.button !== 0 || this.tabs.length < 2 || this.gesture) return;
          const panel = event.target.closest('.go-technology-tabs__panel.is-active');
          if (!panel || !this.root.contains(panel)) return;
          if (event.target.closest('a, button, input, select, textarea, [contenteditable=""], [contenteditable="true"]')) return;

          this.gesture = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            axis: null,
            index: this.currentIndex
          };
          this.stopAutoplay();
          if (event.pointerType === 'mouse') event.preventDefault();
          this.root.setPointerCapture(event.pointerId);
        }, { signal: this.signal });

        this.root.addEventListener('pointermove', (event) => {
          const gesture = this.updateGesture(event);
          if (gesture && gesture.axis === 'x' && event.cancelable) event.preventDefault();
        }, { signal: this.signal });

        this.root.addEventListener('pointerup', (event) => {
          const gesture = this.updateGesture(event);
          if (!gesture) return;
          const deltaX = event.clientX - gesture.x;
          const deltaY = event.clientY - gesture.y;
          const shouldSwitch = gesture.axis === 'x' && gesture.index === this.currentIndex &&
            Math.abs(deltaX) >= 50 && Math.abs(deltaX) > Math.abs(deltaY);
          this.clearGesture(false);
          if (shouldSwitch) this.activateAdjacent(deltaX < 0 ? 1 : -1, true);
          this.startAutoplay();
        }, { signal: this.signal });

        const cancelGesture = (event) => {
          if (this.gesture && event.pointerId === this.gesture.pointerId) this.clearGesture();
        };
        this.root.addEventListener('pointercancel', cancelGesture, { signal: this.signal });
        this.root.addEventListener('lostpointercapture', cancelGesture, { signal: this.signal });
        this.root.addEventListener('dragstart', (event) => {
          const panel = event.target.closest('.go-technology-tabs__panel.is-active');
          if (panel && this.root.contains(panel)) event.preventDefault();
        }, { signal: this.signal });
      }
    }

    updateGesture(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.pointerId) return null;
      if (!gesture.axis) {
        const deltaX = Math.abs(event.clientX - gesture.x);
        const deltaY = Math.abs(event.clientY - gesture.y);
        if (Math.max(deltaX, deltaY) >= 8) {
          gesture.axis = deltaX > deltaY ? 'x' : 'y';
          this.root.classList.toggle('is-dragging', gesture.axis === 'x');
        }
      }
      return gesture;
    }

    clearGesture(restartAutoplay = true) {
      const gesture = this.gesture;
      this.gesture = null;
      this.root.classList.remove('is-dragging');
      if (gesture && this.root.hasPointerCapture(gesture.pointerId)) {
        this.root.releasePointerCapture(gesture.pointerId);
      }
      if (gesture && restartAutoplay) this.startAutoplay();
    }

    activateAdjacent(direction, userInitiated = false) {
      const nextIndex = this.normalizedAdjacentIndex(this.currentIndex + direction);
      if (nextIndex === this.currentIndex) return;
      this.activate(nextIndex, { userInitiated, scroll: true });
    }

    activate(index, options = {}) {
      if (!this.tabs.length || !this.panels.length) return;
      const nextIndex = this.clampIndex(index);

      if (nextIndex === this.currentIndex) {
        if (options.focus) this.tabs[nextIndex].focus({ preventScroll: true });
        if (options.scroll !== false) this.scrollTabIntoView(nextIndex, true);
        if (options.userInitiated) this.restartAutoplay();
        return;
      }

      window.clearTimeout(this.transitionTimer);
      window.cancelAnimationFrame(this.activationFrame);
      this.clearGesture(false);
      const previousIndex = this.currentIndex;
      const previousPanel = this.panels[previousIndex];
      const nextPanel = this.panels[nextIndex];
      const animateOutgoing = this.duration > 0 && this.transitionType !== 'none';

      this.panels.forEach((panel, panelIndex) => {
        if (panelIndex !== previousIndex && panelIndex !== nextIndex) {
          panel.classList.remove('is-active', 'is-exiting');
          panel.hidden = true;
          panel.setAttribute('aria-hidden', 'true');
        }
      });

      if (previousPanel) {
        previousPanel.classList.remove('is-active');
        previousPanel.classList.toggle('is-exiting', animateOutgoing);
        previousPanel.setAttribute('aria-hidden', 'true');
        if (!animateOutgoing) previousPanel.hidden = true;
      }

      nextPanel.hidden = false;
      nextPanel.classList.remove('is-active', 'is-exiting');
      nextPanel.setAttribute('aria-hidden', 'false');

      this.currentIndex = nextIndex;
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === nextIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      if (this.duration > 0 && this.transitionType !== 'none') {
        this.activationFrame = window.requestAnimationFrame(() => {
          if (this.signal.aborted || this.currentIndex !== nextIndex) return;
          this.activationFrame = window.requestAnimationFrame(() => {
            this.activationFrame = null;
            if (!this.signal.aborted && this.currentIndex === nextIndex) nextPanel.classList.add('is-active');
          });
        });
      } else {
        nextPanel.classList.add('is-active');
      }

      if (animateOutgoing && previousPanel) {
        this.transitionTimer = window.setTimeout(() => {
          previousPanel.hidden = true;
          previousPanel.classList.remove('is-exiting');
        }, this.duration + 40);
      }

      if (options.focus) this.tabs[nextIndex].focus({ preventScroll: true });
      if (options.scroll !== false) this.scrollTabIntoView(nextIndex, true);
      this.updateArrowState();

      if (options.userInitiated) this.restartAutoplay();
    }

    scrollTabIntoView(index, smooth) {
      if (!this.viewport || !this.tabs[index]) return;
      const tab = this.tabs[index];
      const viewportRect = this.viewport.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      let targetLeft = this.viewport.scrollLeft;

      if (this.centerActiveTab) {
        targetLeft += tabRect.left - viewportRect.left - (viewportRect.width - tabRect.width) / 2;
      } else if (tabRect.left < viewportRect.left) {
        targetLeft += tabRect.left - viewportRect.left;
      } else if (tabRect.right > viewportRect.right) {
        targetLeft += tabRect.right - viewportRect.right;
      } else {
        return;
      }

      this.viewport.scrollTo({
        left: targetLeft,
        behavior: smooth && !this.reducedMotionQuery.matches ? 'smooth' : 'auto'
      });
    }

    scrollActiveTab(smooth) {
      this.scrollTabIntoView(this.currentIndex, smooth);
    }

    updateArrowState() {
      const disablePrevious = this.tabs.length < 2 || (!this.loopNavigation && this.currentIndex === 0);
      const disableNext = this.tabs.length < 2 || (!this.loopNavigation && this.currentIndex === this.tabs.length - 1);
      if (this.previousButton) this.previousButton.disabled = disablePrevious;
      if (this.nextButton) this.nextButton.disabled = disableNext;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled || this.tabs.length < 2 || document.hidden || this.hovering || this.gesture) return;

      this.autoplayTimer = window.setTimeout(() => {
        if (!this.loopNavigation && this.currentIndex === this.tabs.length - 1) return;
        this.activateAdjacent(1, false);
        this.startAutoplay();
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    showBlock(blockId) {
      const panelIndex = this.panels.findIndex((panel) => panel.dataset.blockId === blockId);
      if (panelIndex < 0) return false;
      this.stopAutoplay();
      this.activate(panelIndex, { scroll: true });
      return true;
    }

    destroy() {
      window.clearTimeout(this.transitionTimer);
      window.cancelAnimationFrame(this.activationFrame);
      this.stopAutoplay();
      this.controller.abort();
      this.clearGesture(false);
    }
  }

  const instances = new Map();

  const findRoots = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-go-tech-tabs]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-go-tech-tabs]'));
    return roots;
  };

  const init = (scope = document) => {
    findRoots(scope).forEach((root) => {
      if (!instances.has(root)) instances.set(root, new TechnologyFeatureTabs(root));
    });
  };

  const unload = (scope) => {
    findRoots(scope).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  window.GoyojoTechnologyTabs = { init, unload, instances };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;
    for (const instance of instances.values()) {
      if (instance.showBlock(blockId)) break;
    }
  });
})();
